// Target Management Types

export type TargetPeriod = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';

export type TargetStatus = 'ACTIVE' | 'COMPLETED' | 'MISSED' | 'EXCEEDED' | 'CANCELLED';

export type AchievementType =
  | 'TARGET_MET'
  | 'TARGET_EXCEEDED'
  | 'STREAK_WEEKLY'
  | 'STREAK_MONTHLY'
  | 'TOP_PERFORMER'
  | 'FASTEST_CLOSURE'
  | 'HIGHEST_DEAL';

export interface ExecutiveTarget {
  id: string;
  userId: string;
  user?: {
    id: string;
    name: string;
    email: string;
    role: string;
    avatar?: string;
  };
  period: TargetPeriod;
  periodStart: string;
  periodEnd: string;
  periodLabel: string;

  // Targets
  revenueTarget: number;
  revenueAchieved: number;
  leadsTarget: number;
  leadsCreated: number;
  leadsConverted: number;
  meetingsTarget: number;
  meetingsHeld: number;
  followUpsTarget: number;
  followUpsDone: number;
  proposalsTarget: number;
  proposalsSent: number;

  // Progress
  revenueProgress: number;
  leadsProgress: number;
  overallProgress: number;

  // Status
  status: TargetStatus;
  isAchieved: boolean;
  achievedAt?: string;

  // Audit
  setById?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;

  // Live stats (populated by API)
  liveStats?: LiveStats;
  snapshots?: TargetSnapshot[];
}

export interface LiveStats {
  revenue: number;
  leadsCreated: number;
  leadsConverted: number;
  meetingsHeld: number;
  followUpsDone: number;
  proposalsSent: number;
}

export interface TargetSnapshot {
  id: string;
  targetId: string;
  snapshotDate: string;
  revenueAchieved: number;
  leadsCreated: number;
  leadsConverted: number;
  meetingsHeld: number;
  followUpsDone: number;
  proposalsSent: number;
  dailyRevenue: number;
  dailyLeads: number;
  dailyMeetings: number;
  dailyFollowUps: number;
  createdAt: string;
}

export interface TargetTemplate {
  id: string;
  name: string;
  description?: string;
  period: TargetPeriod;
  role: string;
  revenueTarget: number;
  leadsTarget: number;
  meetingsTarget: number;
  followUpsTarget: number;
  proposalsTarget: number;
  revenueWeight: number;
  leadsWeight: number;
  meetingsWeight: number;
  followUpsWeight: number;
  proposalsWeight: number;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TargetAchievement {
  id: string;
  userId: string;
  achievementType: AchievementType;
  achievedAt: string;
  periodLabel: string;
  metricType: string;
  targetValue: number;
  achievedValue: number;
  percentageOver: number;
  badgeAwarded?: string;
  bonusEligible: boolean;
  managerNotes?: string;
  createdAt: string;
}

export interface LeaderboardEntry {
  rank: number;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    avatar?: string;
  };
  liveStats: LiveStats;
  revenueProgress: number;
  leadsProgress: number;
  overallProgress: number;
}

export interface TeamMember {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    avatar?: string;
  };
  target: ExecutiveTarget | null;
  liveStats: LiveStats;
  overallProgress: number;
  status: 'EXCEEDED' | 'ON_TRACK' | 'AT_RISK' | 'BEHIND';
}

export interface TeamOverview {
  summary: {
    totalMembers: number;
    exceeded: number;
    onTrack: number;
    atRisk: number;
    behind: number;
    totalRevenue: number;
    totalLeads: number;
    avgProgress: number;
  };
  members: TeamMember[];
  periodLabel: string;
}

// API Request/Response types
export interface SetTargetRequest {
  userId: string;
  period?: TargetPeriod;
  periodStart?: string;
  revenueTarget?: number;
  leadsTarget?: number;
  meetingsTarget?: number;
  followUpsTarget?: number;
  proposalsTarget?: number;
  notes?: string;
}

export interface BulkSetTargetsRequest {
  targets: SetTargetRequest[];
  period?: TargetPeriod;
  periodStart?: string;
}

export interface TargetsResponse {
  success: boolean;
  data: ExecutiveTarget[];
  meta: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CurrentTargetResponse {
  success: boolean;
  data: ExecutiveTarget;
}

export interface LeaderboardResponse {
  success: boolean;
  data: LeaderboardEntry[];
  meta: {
    period: TargetPeriod;
    periodLabel: string;
    metric: string;
    totalParticipants: number;
  };
}

export interface TeamOverviewResponse {
  success: boolean;
  data: TeamOverview;
}

// UI Helper types
export interface TargetMetric {
  key: keyof LiveStats;
  label: string;
  targetKey: keyof ExecutiveTarget;
  icon: string;
  color: string;
  format: 'currency' | 'number' | 'percent';
}

export const TARGET_METRICS: TargetMetric[] = [
  { key: 'revenue', label: 'Revenue', targetKey: 'revenueTarget', icon: 'IndianRupee', color: 'green', format: 'currency' },
  { key: 'leadsCreated', label: 'Leads Created', targetKey: 'leadsTarget', icon: 'Users', color: 'blue', format: 'number' },
  { key: 'meetingsHeld', label: 'Meetings', targetKey: 'meetingsTarget', icon: 'Calendar', color: 'purple', format: 'number' },
  { key: 'followUpsDone', label: 'Follow-ups', targetKey: 'followUpsTarget', icon: 'Phone', color: 'orange', format: 'number' },
  { key: 'proposalsSent', label: 'Proposals', targetKey: 'proposalsTarget', icon: 'FileText', color: 'indigo', format: 'number' },
];

export const PERIOD_OPTIONS = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'YEARLY', label: 'Yearly' },
];
